import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import type { DBMeetingTranscript } from "@/lib/db-types";
import { formatDate } from "@/lib/format";
import { entryIndexAtOffset, filterEntries, formatOffset, speakingSegments } from "@/lib/meeting-summary-calc";
import { cn } from "@/lib/utils";

interface TranscriptPaneProps {
  transcript: DBMeetingTranscript;
  /** Når satt, scrolles transkripsjonen til innslaget ved dette tidspunktet. */
  jumpTo: { offsetMs: number; nonce: number } | null;
  /** Tidspunkter (ms) beslutninger og punkter i referatet viser til; innslagene markeres. */
  markedOffsets?: number[];
}

/** Venstre side: tidslinje over hvem som snakket, søk, og innslagene med klokkeslett. */
export default function TranscriptPane({ transcript, jumpTo, markedOffsets = [] }: TranscriptPaneProps) {
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState<number | null>(null);
  const refs = useRef<(HTMLLIElement | null)[]>([]);
  const entries = transcript.entries;
  const indexed = useMemo(() => entries.map((entry, index) => ({ entry, index })), [entries]);
  const visible = useMemo(() => {
    const keep = new Set(filterEntries(entries, query));
    return indexed.filter(({ entry }) => keep.has(entry));
  }, [indexed, entries, query]);
  const marked = useMemo(() => new Set(markedOffsets.map((o) => entryIndexAtOffset(entries, o))), [markedOffsets, entries]);
  const segments = useMemo(() => speakingSegments(entries, transcript.participants), [entries, transcript.participants]);

  useEffect(() => {
    if (!jumpTo) return;
    setQuery("");
    const index = entryIndexAtOffset(entries, jumpTo.offsetMs);
    setHighlight(index >= 0 ? index : null);
    // Venter til listen er tegnet uten filter før den scroller.
    const id = requestAnimationFrame(() => refs.current[index]?.scrollIntoView?.({ block: "center", behavior: "smooth" }));
    return () => cancelAnimationFrame(id);
  }, [jumpTo, entries]);

  const sideOf = new Map(transcript.participants.map((p) => [p.name, p.side]));
  return (
    <section aria-label="Transkripsjon" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-xl font-semibold">Transkripsjon</h2>
        <span className="rounded-pill bg-muted px-2.5 py-0.5 text-xs">Slettes {formatDate(transcript.purge_after)}</span>
      </div>
      <div role="img" aria-label="Hvem som snakket gjennom møtet" className="relative h-5 border border-border bg-muted/40">
        {segments.map((s, i) => (
          <span
            key={i}
            title={s.participant}
            className={cn("absolute top-0 h-full", s.side === "solvrev" ? "bg-foreground/70" : "bg-primary/50")}
            style={{ left: `${s.from * 100}%`, width: `${s.width * 100}%` }}
          />
        ))}
      </div>
      <ul className="flex flex-wrap gap-3 text-xs text-muted-foreground">
        {transcript.participants.map((p) => (
          <li key={p.name}>
            <span className={cn("mr-1 inline-block h-2 w-2", p.side === "solvrev" ? "bg-foreground/70" : "bg-primary/50")} aria-hidden />
            {p.name} ({p.side === "solvrev" ? "Sølvrev" : "kunde"})
          </li>
        ))}
      </ul>
      <Input aria-label="Søk i transkripsjonen" placeholder="Søk i teksten" value={query} onChange={(e) => setQuery(e.target.value)} />
      <ul className="max-h-[60vh] space-y-2 overflow-y-auto border-[1.5px] border-foreground bg-card p-3 text-sm">
        {visible.length === 0 && <li className="text-muted-foreground">Ingen treff.</li>}
        {visible.map(({ entry: e, index }) => {
          return (
            <li
              key={index}
              ref={(el) => {
                refs.current[index] = el;
              }}
              className={cn("rounded-sm p-1.5", sideOf.get(e.participant) === "solvrev" && "bg-muted/50", highlight === index && "bg-primary/15", marked.has(index) && "border-l-2 border-primary")}
              data-marked={marked.has(index) ? "true" : undefined}
            >
              <span className="mr-2 font-mono text-xs text-muted-foreground">{formatOffset(e.start_offset_ms)}</span>
              <span className={cn("mr-1.5 font-medium", sideOf.get(e.participant) === "solvrev" ? "" : "text-primary")}>{e.participant}</span>
              <span>{e.text}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
